// /f/{id}: a file on Share (RFC-054 "Web", byj-1wv.70.4), reworked in
// /shares' way (byj-1wv.51.17): the version shown — picked in the head — and
// its comments on the left; beside them, as a share's side panel (Panel), a
// folder's files, who sees it and its versions. Its owner shares it from one
// dialog (components/FileShare) and edits its name and description in another
// (FileDescribe, as a template's); its next version, deleting a version or the
// whole of it are under its ⋯ and its Versions' heading, deleting asked first.
// Anyone else is told how they came to see it. The lines that get it, and put
// its next version, are in the head's From a terminal.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  api,
  bytesOf,
  fileContentURL,
  folderEntryURL,
  type FileComment,
  type FolderEntry,
  type ShareFileDetail,
} from "../api";
import { useConfirm } from "../components/Confirm";
import { FileComments, marksOf } from "../components/FileComments";
import { FileDescribe } from "../components/FileDescribe";
import { FilePreview, type LineComments } from "../components/FilePreview";
import { FileShare, fileShareId } from "../components/FileShare";
import { useOpenInvites } from "../components/OpenInvites";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { Refused, refusalOf, type Refusal } from "../components/Refused";
import { RowMenu, type MenuItem } from "../components/RowMenu";
import { initialOf } from "../components/Rows";
import { Panel } from "../components/SidePanel";
import { download } from "../download";
import { navigate } from "../router";
import { ago, t, whenMinute, word, phrases } from "../i18n";
import { fileBytes, reviewWords, type FileApi } from "../files";
import { TerminalCommands } from "../components/TerminalCommands";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/**
 * A folder's files to pick, as the first of the side panel's: beside what is
 * shown on a wide screen, so the file picked has the column's width, and over
 * it on a narrow one (the layout's order).
 */
function FolderTree({ entries, error, picked, onPick }: {
  entries: FolderEntry[] | null;
  error: string | null;
  picked: string | null;
  onPick: (path: string) => void;
}) {
  return (
    <Panel label={t("files:fileInFolder")} count={entries ? entries.filter((e) => !e.dir).length : undefined} className="file-panel file-tree-panel">
      {error ? (
        <p className="error small flush">{t("common:error", { m: error })}</p>
      ) : !entries ? (
        <p className="muted small flush">{t("common:loading")}</p>
      ) : (
        <nav aria-label={t("files:fileInFolder")} className="file-tree" data-testid="file-tree">
          <ul>
            {entries.map((e) => {
              const depth = e.path.split("/").length - 1;
              const base = e.path.split("/").pop();
              return (
                <li key={e.path} style={{ paddingInlineStart: `${depth * 0.9}rem` }}>
                  {e.dir ? (
                    <span className="muted file-tree-dir">{base}/</span>
                  ) : (
                    <button className={`link${e.path === picked ? " current" : ""}`} aria-current={e.path === picked} onClick={() => onPick(e.path)}>
                      {base}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </Panel>
  );
}

/** The file of a folder picked: its path, size and Download on the bar over it, its lines commented on. */
function FolderEntry({ id, n, entries, picked, comments }: {
  id: string;
  n: number;
  entries: FolderEntry[] | null;
  picked: string | null;
  comments: (path: string) => LineComments;
}) {
  if (!entries) return <p className="muted">{t("common:loading")}</p>;
  const sel = entries.find((e) => e.path === picked);
  if (!sel) return <p className="muted empty-card">{t("files:filePickEntry")}</p>;
  return (
    <FilePreview
      url={folderEntryURL(id, n, sel.path)}
      name={sel.path}
      comments={comments(sel.path)}
      head={
        <>
          <code className="file-bar-path">{sel.path}</code>
          <span className="muted small">{fileBytes(sel.bytes)}</span>
        </>
      }
      actions={
        <a className="button small" href={folderEntryURL(id, n, sel.path, true)} download>
          {t("files:fileDownload")}
        </a>
      }
    />
  );
}

/** Who sees it, to its owner: its viewers and classes, changed in the Share dialog — `open` while its Manage opened it. */
function SeenBy({ detail, open, onShare }: { detail: ShareFileDetail; open: boolean; onShare: () => void }) {
  const viewers = detail.viewers ?? [];
  const classes = detail.classes ?? [];
  return (
    <Panel
      label={t("files:filePeople")}
      count={viewers.length + classes.length}
      className="file-panel"
      testid="file-seen-by"
      action={
        <button className="small" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? fileShareId : undefined} onClick={onShare}>
          {t("files:fileManage")}
        </button>
      }
    >
      {viewers.length === 0 && classes.length === 0 ? (
        <p className="muted small flush">{t("files:fileOnlyYou")}</p>
      ) : (
        // each marked as a template's people and classes are (initialOf)
        <ul className="file-seen">
          {viewers.map((u) => (
            <li key={u.id} className="row">
              <span className="kind-box" aria-hidden="true">
                {initialOf(u.name || u.email)}
              </span>
              <span className="grow file-name-strong">{u.name || u.email}</span>
            </li>
          ))}
          {classes.map((c) => (
            <li key={c.id} className="row">
              <span className="kind-box" aria-hidden="true">
                {initialOf(c.name)}
              </span>
              <span className="grow">{c.name}</span>
              <code className="small muted">{c.code}</code>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** How someone else came to see it: an invite, a class they are in, or a hand-in to their class; a hand-in's own owner is told too. */
function HowYouSee({ detail }: { detail: ShareFileDetail }) {
  const f = detail.file;
  const lines: ReactNode[] = [];
  if (f.hand_in) {
    lines.push(
      detail.role === "instructor" ? (
        <Link to={`/c/${encodeURIComponent(f.hand_in.class)}/a/${encodeURIComponent(f.hand_in.key)}`}>{t("files:fileHandedInTo", { cls: f.hand_in.name, title: f.hand_in.title })}</Link>
      ) : (
        t("files:fileHandedInTo", { cls: f.hand_in.name, title: f.hand_in.title })
      ),
    );
    if (f.expires_at) lines.push(t("files:fileKeptUntil", { w: whenMinute(f.expires_at) }));
  } else if (detail.role === "viewer") lines.push(t("files:fileSharedWithYou", { owner: f.owner }));
  else if (detail.role === "class") lines.push(f.via?.startsWith("class:") ? t("files:fileGivenToClass", { owner: f.owner, code: f.via.slice("class:".length) }) : t("files:fileRole.class")!);
  if (lines.length === 0) return null;
  return (
    <Panel label={detail.role === "owner" ? t("files:filesHandedIn") : t("files:fileHowYouSee")} className="file-panel" testid="file-hand-in">
      {lines.map((l, i) => (
        <p key={i} className="small flush">
          {l}
        </p>
      ))}
    </Panel>
  );
}

/**
 * A hand-in's check (byj-1wv.136): to its student, whether its class's
 * instructor checked it or returned it for changes, with their mark and the
 * version they read; to that instructor, the same with Mark checked, Return
 * for changes, a mark and Take it back. An instructor had checked homework
 * by comments alone, which nothing counted.
 */
function HandInCheck({ detail, client, onReviewed }: { detail: ShareFileDetail; client: FileApi; onReviewed: (r: ShareFileDetail["file"]["review"]) => void }) {
  const f = detail.file;
  const instructor = detail.role === "instructor";
  const [mark, setMark] = useState(f.review?.mark ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!f.hand_in || (!instructor && detail.role !== "owner")) return null;
  const r = f.review;
  const set = async (state: "checked" | "returned") => {
    setBusy(true);
    setError(null);
    try {
      onReviewed(await client.reviewHandIn(f.id, state, mark.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const takeBack = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.clearHandInReview(f.id);
      onReviewed(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel label={t("files:handInCheck")} className="file-panel" testid="hand-in-check">
      {r ? (
        <>
          <p className="small flush" data-testid="hand-in-review">
            <span className={`chip ${r.state === "checked" ? "chip-ok" : "chip-warn"}`}>{reviewWords(r, f.latest)}</span>
          </p>
          <p className="small muted flush" title={whenMinute(r.at)}>
            {t("files:reviewedBy", { state: word("files:reviewState", r.state), name: r.by.name, when: ago(r.at) })} · {t("files:reviewOfVersion", { n: r.version })}
          </p>
        </>
      ) : (
        <p className="small muted flush">{t("files:notChecked")}</p>
      )}
      {instructor && f.latest > 0 && (
        <div className="stack hand-in-check-form">
          <input type="text" aria-label={t("files:markLabel")} placeholder={t("files:markLabel")} value={mark} maxLength={40} onChange={(e) => setMark(e.target.value)} />
          <div className="row wrap">
            <button className="small primary" disabled={busy} onClick={() => void set("checked")}>
              {t("files:markChecked")}
            </button>
            <button className="small" disabled={busy} onClick={() => void set("returned")}>
              {t("files:returnForChanges")}
            </button>
            {r && (
              <button className="small" disabled={busy} onClick={() => void takeBack()}>
                {t("files:takeBackReview")}
              </button>
            )}
          </div>
        </div>
      )}
      {error && <p className="error small flush">{t("common:error", { m: error })}</p>}
    </Panel>
  );
}

export function FilePage({ id, client = api, me, thread = null }: {
  id: string;
  client?: FileApi;
  /** the person viewing: who began a thread resolves it */
  me?: { id: string };
  /** the thread a link names (?thread=), lit and brought into view */
  thread?: string | null;
}) {
  const [detail, setDetail] = useState<ShareFileDetail | null>(null);
  const [comments, setComments] = useState<FileComment[]>([]);
  // the line picked to begin a thread on, and in a folder the file picked
  const [line, setLine] = useState<number | undefined>(undefined);
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // the Share dialog open, and which button opened it — the head's Share or
  // the panel's Manage — so the focus goes back to that one as it closes
  const [sharing, setSharing] = useState<"head" | "panel" | null>(null);
  // its open links, read while Share is open, and those made on this page,
  // kept across its openings: the relay cannot show them again (byj-1wv.76)
  const open = useOpenInvites({ kind: "files", id }, sharing !== null, "", client);
  const [shown, setShown] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [refused, setRefused] = useState<Refusal | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const [ask, dialog] = useConfirm();
  const reload = useCallback(async () => {
    setDetail(await client.file(id));
  }, [client, id]);
  useEffect(() => {
    reload().catch((err: unknown) => setError(errText(err)));
  }, [reload]);
  const readComments = useCallback(async () => {
    setComments(await client.fileComments(id));
  }, [client, id]);
  useEffect(() => {
    readComments().then(
      () => undefined,
      () => setComments([]),
    );
  }, [readComments]);
  // a thread a link names: its version shown, and in a folder its file
  const [followed, setFollowed] = useState(false);
  useEffect(() => {
    if (!thread || followed) return;
    const c = comments.find((x) => x.id === thread);
    if (!c) return;
    const root = c.parent_id ? (comments.find((x) => x.id === c.parent_id) ?? c) : c;
    setShown(root.version);
    if (root.path) setPicked(root.path);
    setFollowed(true);
  }, [thread, comments, followed]);
  // a folder's files, of the version shown: the tree in the side panel and the file picked beside it
  const folderN = detail?.file.kind === "folder" ? (shown ?? detail.file.latest) : 0;
  const [entries, setEntries] = useState<FolderEntry[] | null>(null);
  const [entriesError, setEntriesError] = useState<string | null>(null);
  useEffect(() => {
    if (!folderN) return;
    setEntries(null);
    setEntriesError(null);
    client.folderEntries(id, folderN).then(
      (r) => {
        setEntries(r.entries);
        // a README is what a folder is read by first, unless a file is picked already (a thread's)
        const readme = r.entries.find((e) => !e.dir && /^readme(\.md|\.txt)?$/i.test(e.path));
        if (readme) setPicked((p) => p ?? readme.path);
      },
      (err: unknown) => setEntriesError(errText(err)),
    );
  }, [client, id, folderN]);

  if (error && !detail) {
    return (
      <PageNotice error={error}>
        <Link to="/files">{t("files:filesTitle")}</Link>
      </PageNotice>
    );
  }
  if (!detail) return <PageLoading />;
  const f = detail.file;
  const owner = detail.role === "owner";
  // latest first; a version not uploaded is its owner's alone to see
  const versions = [...detail.versions].sort((a, b) => b.n - a.n);
  const uploaded = versions.filter((x) => x.uploaded);
  const n = shown ?? f.latest;
  const v = detail.versions.find((x) => x.n === n && x.uploaded);
  // the comments on the version and file shown, and how many threads are elsewhere
  const here = (path: string) => comments.filter((c) => c.version === n && (c.path ?? "") === path);
  const placeShown = f.kind === "folder" ? picked : "";
  const shownComments = placeShown === null ? [] : here(placeShown);
  const others = comments.filter((c) => !c.parent_id).length - shownComments.filter((c) => !c.parent_id).length;
  const lineComments = (path: string): LineComments => ({
    marks: marksOf(here(path)),
    selected: line,
    onLine: (k) => setLine(k),
  });
  const canResolve = (root: FileComment) => owner || detail.role === "instructor" || root.author.id === me?.id;

  const removeVersion = async (k: number) => {
    if (!(await ask(t("files:fileDeleteVersionAsk", { n: k }), { action: t("files:fileDeleteVersion", { n: k }), danger: true }))) return;
    try {
      await client.deleteFileVersion(f.id, k);
      if (shown === k) setShown(null);
      await reload();
    } catch (err) {
      setError(errText(err));
    }
  };
  const remove = async () => {
    if (!(await ask(t("files:fileDeleteAsk", { name: f.name }), { action: t("files:fileDelete"), danger: true }))) return;
    try {
      await client.deleteFile(f.id);
      navigate("/files");
    } catch (err) {
      setError(errText(err));
    }
  };
  const next = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setRefused(null);
    setUploading(true);
    try {
      await client.uploadFile(f.id, await bytesOf(file));
      setShown(null);
      await reload();
    } catch (err) {
      const r = refusalOf(err, file.size);
      if (r) setRefused(r);
      else setError(errText(err));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };
  const fileMenu: MenuItem[] = owner
    ? [
        { label: t("files:fileEdit"), onSelect: () => setEditing(true) },
        ...(f.kind === "file" ? [{ label: t("files:fileNewVersion"), onSelect: () => input.current?.click() }] : []),
        { label: t("files:fileDelete"), onSelect: () => void remove(), danger: true },
      ]
    : [];
  const versionMenu = (k: number, isUploaded: boolean): MenuItem[] => [
    ...(isUploaded && k !== n ? [{ label: t("files:fileShow", { n: k }), onSelect: () => setShown(k) }] : []),
    ...(isUploaded ? [{ label: t("files:fileDownloadVersion", { n: k }), onSelect: () => download(fileContentURL(f.id, k, true)) }] : []),
    ...(owner ? [{ label: t("files:fileDeleteVersion", { n: k }), onSelect: () => void removeVersion(k), danger: true }] : []),
  ];
  // the lines a terminal gets it with, and, its owner's, puts its next version with
  const terminal: [string, string][] = [
    [t("files:fileTerminalGet"), t("files:fileGetCommand", { id: f.id })],
    ...(owner ? ([[t("files:fileTerminalNext"), t("files:fileNextCommand", { id: f.id })]] as [string, string][]) : []),
  ];

  return (
    <Page className="file-page">
      <PageHead
        title={f.name}
        long
        meta={
          <>
            {f.kind === "folder" && <span className="chip">{t("files:fileFolder")}</span>}
            <span className="chip">{word("files:fileRole", detail.role) ?? detail.role}</span>
            {uploaded.length > 1 ? (
              <select className="small file-version-pick" aria-label={t("files:fileVersionPick")} value={v?.n ?? ""} onChange={(e) => setShown(Number(e.target.value))}>
                {uploaded.map((x) => (
                  <option key={x.n} value={x.n}>
                    {phrases.fileVersionOption(x.n, x.n === f.latest)}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`chip${f.latest ? "" : " chip-warn"}`}>{f.latest ? t("files:fileVersion", { n: f.latest }) : t("files:fileNoVersion")}</span>
            )}
          </>
        }
        actions={
          <>
            {v && (
              <a className="button small" href={fileContentURL(f.id, v.n, true)} download>
                {t("files:fileDownload")}
              </a>
            )}
            {uploaded.length > 0 && <TerminalCommands lines={terminal} />}
            {owner && (
              <button
                className="primary small"
                aria-haspopup="dialog"
                aria-expanded={sharing === "head"}
                aria-controls={sharing === "head" ? fileShareId : undefined}
                onClick={() => setSharing("head")}
              >
                {t("files:fileShare")}
              </button>
            )}
            {owner && <RowMenu label={t("shares:moreFor", { title: f.name })} items={fileMenu} />}
          </>
        }
      />
      <p className={f.description ? "pre-wrap" : "muted"} data-testid="file-description">
        {f.description || t("files:fileNoDescription")}
      </p>
      {!owner && <p className="muted small">{t("files:fileBy", { owner: f.owner })}</p>}
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {refused && <Refused r={refused} />}
      {owner && f.kind === "file" && <input ref={input} type="file" hidden data-testid="file-next-input" onChange={(e) => void next(e.target.files?.[0])} />}

      <div className="file-layout">
        <div className="file-main">
          {v ? (
            <section aria-label={t("files:fileVersion", { n: v.n })} className="stack file-shown" data-testid="file-shown">
              {f.kind === "folder" ? (
                <FolderEntry id={f.id} n={v.n} entries={entries} picked={picked} comments={lineComments} />
              ) : (
                <FilePreview
                  url={fileContentURL(f.id, v.n)}
                  name={f.name}
                  kind={v.preview}
                  bytes={v.bytes}
                  comments={v.preview === "text" ? lineComments("") : undefined}
                />
              )}
            </section>
          ) : (
            <p className="muted empty-card">{t("files:fileNoVersion")}</p>
          )}
        </div>
        {/* its comments, under the version shown; narrow, under the side panel too, so who sees it is not below every thread */}
        <div className="file-talk">
          {v && (f.kind === "folder" ? picked !== null : v.preview === "text") && (
            <FileComments
              comments={shownComments}
              others={others}
              line={line}
              path={placeShown || undefined}
              lit={thread}
              canResolve={canResolve}
              onCancel={() => setLine(undefined)}
              begin={async (k, body) => {
                await client.commentOnFile(f.id, { version: v.n, path: placeShown || undefined, line: k, body });
                setLine(undefined);
                await readComments();
              }}
              reply={async (root, body) => {
                await client.commentOnFile(f.id, { parent_id: root.id, body });
                await readComments();
              }}
              resolve={async (root, resolved) => {
                try {
                  await client.resolveFileComment(f.id, root.id, resolved);
                  await readComments();
                } catch (err) {
                  setError(errText(err));
                }
              }}
            />
          )}
        </div>

        <aside className="file-side">
          {f.kind === "folder" && v && (
            <FolderTree entries={entries} error={entriesError} picked={picked} onPick={(p) => (setPicked(p), setLine(undefined))} />
          )}
          <HowYouSee detail={detail} />
          <HandInCheck detail={detail} client={client} onReviewed={(r) => setDetail((d) => (d ? { ...d, file: { ...d.file, review: r } } : d))} />
          {owner && <SeenBy detail={detail} open={sharing === "panel"} onShare={() => setSharing("panel")} />}
          {versions.length > 0 && (
            <Panel
              label={t("files:fileVersions")}
              count={uploaded.length}
              className="file-panel"
              // a file's next version chosen here; a folder's is put from a terminal (the head's From a terminal)
              action={
                owner &&
                f.kind === "file" && (
                  <button className="small" disabled={uploading} onClick={() => input.current?.click()}>
                    {uploading ? t("files:fileUploading", { name: f.name }) : t("files:fileUploadNext")}
                  </button>
                )
              }
            >
              <ul className="file-versions" data-testid="file-versions">
                {versions.map((x) => (
                  <li key={x.n} className={x.n === n && x.uploaded ? "is-shown" : undefined}>
                    <span className="file-version-main">
                      {x.uploaded && x.n !== n ? (
                        <button className="link" onClick={() => setShown(x.n)}>
                          <strong>{t("files:fileVersion", { n: x.n })}</strong>
                        </button>
                      ) : (
                        <strong>{t("files:fileVersion", { n: x.n })}</strong>
                      )}
                      {x.n === f.latest && <span className="chip chip-ok">{t("files:fileLatest")}</span>}
                      {x.n === n && x.uploaded && <span className="chip">{t("files:fileShowing")}</span>}
                      {x.late && <span className="chip chip-warn">{t("files:fileLate")}</span>}
                      {!x.uploaded && <span className="chip chip-warn">{t("recordings:recordingNotUploaded")}</span>}
                    </span>
                    <span className="muted small file-version-meta">
                      {fileBytes(x.bytes)} · {whenMinute(x.created_at)}
                    </span>
                    <RowMenu label={t("shares:moreFor", { title: t("files:fileVersion", { n: x.n }) })} items={versionMenu(x.n, x.uploaded)} />
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </aside>
      </div>

      {editing && (
        <FileDescribe
          file={f}
          save={async (patch) => {
            const next = await client.describeFile(f.id, patch);
            setDetail((d) => (d ? { ...d, file: { ...d.file, ...next } } : d));
          }}
          onClose={() => setEditing(false)}
        />
      )}
      {sharing && (
        <FileShare
          id={f.id}
          detail={detail}
          client={client}
          reload={reload}
          open={open}
          onClose={() => setSharing(null)}
        />
      )}
      {dialog}
    </Page>
  );
}
